import React from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { buildRenderedEmailDocument } from 'components/Activities/utils/renderedEmail';
import { SpeedToLeadTouchpointEvent } from 'types/recommendations';
import { formatCountWithUnit } from 'utils/helpers/string';

import {
  SPEED_TO_LEAD_CHANNEL,
  SPEED_TO_LEAD_COMMUNITY_OUTBOUND_DIRECTION,
  SPEED_TO_LEAD_UNANSWERED_CALL_OUTCOMES,
  SPEED_TO_LEAD_VOICEMAIL_DURATION_LABEL,
} from './constants';
import { getChannelLabel } from './speedToLeadOverallUtils';
import SpeedToLeadTouchpointAudio from './SpeedToLeadTouchpointAudio';
import { downloadHtml, formatMediaDuration } from './touchpointUtils';

const TouchpointBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const EmailHeader = styled.div`
  display: flex;
  height: 36px;
  box-sizing: border-box;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px;
  border-bottom: 1px solid var(--line-gray);
  background: var(--white);
`;

const MessageContent = styled.div`
  display: flex;
  gap: 16px;
  padding: 30px 90px;
  background: var(--neutral-colors-gray-10);
`;

const DurationDetails = styled.div`
  display: flex;
  width: 62px;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding-right: 16px;
  border-right: 3px solid var(--line-gray);
  color: var(--text-primary);
  font-size: 11px;
  text-align: right;
`;

const DurationType = styled.span`
  font-family: var(--font-bold);
`;

const DurationValue = styled.span`
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
`;

const MessageContentBody = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 12px;
`;

const Subject = styled.div`
  color: var(--text-primary);
  font-family: var(--font-regular);
  font-size: 12px;
`;

const SubjectLabel = styled.span`
  font-family: var(--font-bold);
`;

const EmailActions = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 12px;
`;

const MessageBody = styled.div`
  color: var(--text-primary);
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const UnansweredCallSummary = styled(MessageBody)`
  padding: 10px 0;
  text-align: center;
`;

const MissingTranscript = styled.div`
  align-self: center;
  color: var(--text-secondary);
  font-size: 12px;
`;

const HtmlFrame = styled.iframe`
  width: 100%;
  height: 280px;
  border: 1px solid var(--line-gray);
  background: var(--white);
`;

const ContentActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const TextAction = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--dark-blue);
  cursor: pointer;
  font-family: var(--font-bold);
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }
`;

const AttachmentLink = styled.a`
  display: inline-flex;
  min-height: 28px;
  box-sizing: border-box;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border: 1px solid var(--active-blue);
  border-radius: 4px;
  color: var(--active-blue);
  font-size: 12px;
  line-height: 18px;
  text-decoration: none;

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .anticon svg {
    display: block;
  }

  &:hover {
    background: var(--background-light-blue);
    color: var(--active-blue);
  }

  &:focus-visible {
    outline: 2px solid var(--dark-blue);
    outline-offset: 2px;
  }
`;

const EmptyState = styled.div`
  padding: 28px 0;
  color: var(--text-secondary);
  font-size: 13px;
  text-align: center;
`;

type Props = {
  event: SpeedToLeadTouchpointEvent;
  eventKey: string | number;
};

const SpeedToLeadTouchpointContent = ({ event, eventKey }: Props) => {
  if (!event.available) {
    return <EmptyState>Touchpoint content is no longer available.</EmptyState>;
  }

  const mediaText = event.ringcentral?.media.find((media) => media.text_content)?.text_content;
  const isSms = event.channel === SPEED_TO_LEAD_CHANNEL.SMS;
  const messageBody =
    event.email?.text_body || (isSms ? mediaText : null) || event.ringcentral?.body_preview || event.body;
  const audioMedia = event.ringcentral?.media.find(
    (media) => media.file_url && (media.content_type?.startsWith('audio/') || media.media_type.includes('recording'))
  );
  const transcript = isSms ? null : mediaText;
  const recordingDuration =
    event.ringcentral?.duration_seconds ??
    event.ringcentral?.media.find((media) => media.duration_seconds != null)?.duration_seconds;
  const durationType =
    event.outcome?.toLowerCase() === SPEED_TO_LEAD_CHANNEL.VOICEMAIL
      ? SPEED_TO_LEAD_VOICEMAIL_DURATION_LABEL
      : getChannelLabel(SPEED_TO_LEAD_CHANNEL.CALL);
  const callOutcome = event.ringcentral?.call_outcome || event.outcome;
  const showUnansweredCallSummary =
    event.channel === SPEED_TO_LEAD_CHANNEL.CALL &&
    SPEED_TO_LEAD_UNANSWERED_CALL_OUTCOMES.has(callOutcome ?? '') &&
    !audioMedia?.file_url &&
    !transcript &&
    !messageBody;
  const callParties =
    event.direction === SPEED_TO_LEAD_COMMUNITY_OUTBOUND_DIRECTION
      ? 'Community called the lead'
      : 'Lead called the community';
  const ringSeconds = recordingDuration != null ? Math.round(recordingDuration) : null;
  const unansweredCallSummary =
    ringSeconds != null && ringSeconds > 0
      ? `${callParties} and the call rang for ${formatCountWithUnit(ringSeconds, 'second')}.`
      : `${callParties}, but the call was not answered.`;
  const htmlFrame = event.email?.html_body ? (
    <HtmlFrame
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      srcDoc={buildRenderedEmailDocument(event.email.html_body)}
      title={`Email touchpoint ${eventKey}`}
    />
  ) : null;
  const displayedMessage = htmlFrame || (messageBody ? <MessageBody>{messageBody}</MessageBody> : null);

  return (
    <TouchpointBody>
      {event.email && (event.email.subject || event.email.html_body) && (
        <EmailHeader>
          {event.email.subject ? (
            <Subject>
              <SubjectLabel>Subject:</SubjectLabel> {event.email.subject}
            </Subject>
          ) : (
            <span />
          )}
          <EmailActions>
            {event.email.html_body && (
              <TextAction
                type="button"
                onClick={() => downloadHtml(event)}
                data-test-id={`speed-to-lead-touchpoint-download-html-${eventKey}`}
              >
                Download HTML
              </TextAction>
            )}
          </EmailActions>
        </EmailHeader>
      )}
      {audioMedia?.file_url && (
        <SpeedToLeadTouchpointAudio key={audioMedia.file_url} eventId={eventKey} source={audioMedia.file_url} />
      )}
      <MessageContent>
        {recordingDuration != null && !showUnansweredCallSummary && (
          <DurationDetails>
            <DurationType>{durationType}</DurationType>
            <DurationValue>{formatMediaDuration(recordingDuration)}</DurationValue>
          </DurationDetails>
        )}
        <MessageContentBody>
          {showUnansweredCallSummary && <UnansweredCallSummary>{unansweredCallSummary}</UnansweredCallSummary>}
          {displayedMessage}
          {transcript && transcript !== messageBody && <MessageBody>{transcript}</MessageBody>}
          {audioMedia?.file_url && !transcript && (
            <MissingTranscript>No transcription is available for this recording.</MissingTranscript>
          )}
          {event.attachments.length > 0 && (
            <ContentActions>
              {event.attachments.map((attachment) => (
                <AttachmentLink
                  href={attachment.file_url}
                  key={attachment.id}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-test-id={`speed-to-lead-touchpoint-attachment-${attachment.id}`}
                >
                  <DownloadOutlined />
                  {attachment.file_name}
                </AttachmentLink>
              ))}
            </ContentActions>
          )}
        </MessageContentBody>
      </MessageContent>
    </TouchpointBody>
  );
};

export default SpeedToLeadTouchpointContent;
